import { useCallback, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * SAB-1004 — upload a team logo.
 *
 * Order of operations is deliberate and the reason is moderation: the image is normalised
 * FIRST, then moderated, then stored. Moderating the original and storing a re-encoded
 * derivative would mean the bytes that were checked are not the bytes that get served.
 *
 * ── WHY RE-ENCODE AT ALL ────────────────────────────────────────────────────
 *
 * A phone photo is 3-4MB and 4032x3024. The bucket cap is 2MB, so an unprocessed upload
 * from a camera roll simply fails -- the common case, failing. Downscaling to a 256px
 * square also makes every surface render the same shape, which is what lets one component
 * (TeamLogo) size it from an 11px chip to a 44px header without per-surface cropping.
 *
 * Output is always JPEG, so the storage path has ONE extension and an upsert genuinely
 * replaces the previous logo. Keying the path on the source extension would leave
 * `<team>.png` behind when someone later uploads a `.jpg`, and the stale file would keep
 * being served to anyone holding the old URL.
 *
 * ── THE CACHE-BUSTER IS LOAD-BEARING ────────────────────────────────────────
 *
 * The path is stable, so the public URL is stable, so a browser that has seen the old logo
 * would keep showing it after an upsert. `?v=<timestamp>` makes the stored URL unique per
 * upload. The user-avatar path already does this; same reason.
 */

const MAX_BYTES = 2 * 1024 * 1024;
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];
const SIDE = 256;

export const TEAM_LOGO_ACCEPT = ACCEPTED.join(',');

/** Centre-crop to a square, downscale to SIDE, re-encode as JPEG. */
export async function normaliseToSquareJpeg(file: File, side = SIDE): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const min = Math.min(bitmap.width, bitmap.height);
  const sx = (bitmap.width - min) / 2;
  const sy = (bitmap.height - min) / 2;

  const canvas = document.createElement('canvas');
  canvas.width = side;
  canvas.height = side;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unavailable');
  ctx.drawImage(bitmap, sx, sy, min, min, 0, 0, side, side);
  bitmap.close?.();

  const blob = await new Promise<Blob | null>(res => canvas.toBlob(res, 'image/jpeg', 0.85));
  if (!blob) throw new Error('Could not encode image');
  return blob;
}

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = reject;
    r.readAsDataURL(blob);
  });

export interface UseTeamLogoUploadArgs {
  teamId: string | null | undefined;
  ownerId: string | null | undefined;
  onDone?: () => void;
  onError?: (message: string) => void;
}

export function useTeamLogoUpload({ teamId, ownerId, onDone, onError }: UseTeamLogoUploadArgs) {
  const [uploading, setUploading] = useState(false);

  const fail = useCallback((m: string) => { console.error('[team-logo]', m); onError?.(m); }, [onError]);

  const upload = useCallback(async (file: File) => {
    if (!teamId || !ownerId) { fail('No team to update.'); return; }

    if (!ACCEPTED.includes(file.type)) { fail('Use a JPEG, PNG or WebP image.'); return; }
    // Checked before decoding: a 40MB file should be refused, not decoded first.
    if (file.size > MAX_BYTES) { fail('Image must be under 2MB.'); return; }

    setUploading(true);
    try {
      let blob: Blob;
      try {
        blob = await normaliseToSquareJpeg(file);
      } catch {
        fail("That image couldn't be read. Try a different file.");
        return;
      }

      // Moderate the BYTES THAT WILL BE STORED, not the original.
      const { data: mod, error: modErr } = await supabase.functions.invoke('moderate-image', {
        body: { imageBase64: await blobToBase64(blob), mimeType: 'image/jpeg' },
      });
      if (modErr) { fail('Unable to verify image. Please try again.'); return; }
      if (!mod?.safe) {
        fail(mod?.reason || 'That image was flagged as inappropriate. Please choose another.');
        return;
      }

      // <uid>/<team_id>.jpg -- the folder is what the storage policy checks.
      const path = `${ownerId}/${teamId}.jpg`;
      const { error: upErr } = await supabase.storage
        .from('team-logos')
        .upload(path, blob, { upsert: true, contentType: 'image/jpeg' });
      if (upErr) { fail('Upload failed. Please try again.'); return; }

      const { data: urlData } = supabase.storage.from('team-logos').getPublicUrl(path);
      const publicUrl = `${urlData.publicUrl}?v=${Date.now()}`;

      const { error: rowErr } = await (supabase as any)
        .from('nfl_teams')
        .update({ logo_url: publicUrl })
        .eq('id', teamId);
      // The row write is what MAKES it the team's logo; a stored file nothing points at is
      // inert, so a failure here is a real failure even though the upload succeeded.
      if (rowErr) { fail('Logo uploaded but could not be saved. Please try again.'); return; }

      onDone?.();
    } finally {
      setUploading(false);
    }
  }, [teamId, ownerId, fail, onDone]);

  return { upload, uploading };
}
